/**
 * RuleChatPanel (B-623) — conversational rule-reviewer.
 *
 * Ask in plain English whether a companion rule exists ("is there a rule that a
 * baseboard gets a paint line?") and, if not, draft it as a proposed rule that
 * enters the triage gate. Every code shown is grounded against the catalog by the
 * backend — the model never invents codes.
 */
import React from 'react';
import { MessageSquare, Send, Loader2, CheckCircle2, HelpCircle, PlusCircle } from 'lucide-react';
import { ruleChatApi, type ChatVerdict } from '../lib/ruleChatApi';

const STATUS_STYLE: Record<string, { icon: React.ReactNode; ring: string }> = {
  exists_active: { icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" />, ring: 'border-emerald-200 bg-emerald-50' },
  exists_proposed: { icon: <HelpCircle className="h-4 w-4 text-amber-600" />, ring: 'border-amber-200 bg-amber-50' },
  not_found: { icon: <PlusCircle className="h-4 w-4 text-sky-600" />, ring: 'border-sky-200 bg-sky-50' },
  needs_clarification: { icon: <HelpCircle className="h-4 w-4 text-slate-500" />, ring: 'border-slate-200 bg-slate-50' },
};

function Term({ label, term }: { label: string; term: ChatVerdict['antecedent'] }) {
  if (!term) return null;
  return (
    <div className="text-xs text-slate-600">
      <span className="font-medium text-slate-500">{label}:</span> “{term.description}” →{' '}
      {term.chosen ? (
        <code className="rounded bg-white px-1 py-0.5 text-slate-800">{term.chosen.code}</code>
      ) : (
        <span className="text-slate-500">
          {term.candidates.length
            ? term.candidates.map((c) => `${c.code} (${c.dsc ?? ''})`).join(' · ')
            : 'no catalog match'}
        </span>
      )}
    </div>
  );
}

export default function RuleChatPanel() {
  const [message, setMessage] = React.useState('');
  const [verdict, setVerdict] = React.useState<ChatVerdict | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saveNote, setSaveNote] = React.useState<string | null>(null);

  const ask = async () => {
    if (!message.trim() || loading) return;
    setLoading(true);
    setError(null);
    setSaveNote(null);
    setVerdict(null);
    try {
      setVerdict(await ruleChatApi.ask(message.trim()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'request failed');
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = async () => {
    if (!verdict?.drafted_rule) return;
    setLoading(true);
    try {
      const r = await ruleChatApi.saveDraft(verdict.drafted_rule);
      setSaveNote(`Saved as proposed — review it in the queue below (${r.name}).`);
      setVerdict({ ...verdict, drafted_rule: null });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setLoading(false);
    }
  };

  const style = verdict ? STATUS_STYLE[verdict.status] ?? STATUS_STYLE.needs_clarification : null;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <MessageSquare className="h-4 w-4 text-indigo-600" />
        Ask the rule base
      </h2>
      <p className="mb-3 text-xs text-slate-500">
        e.g. “is there a rule that a baseboard gets a paint line?” or “if there’s hardwood and a
        door, add a detach &amp; reset door slab — add it”. Codes are grounded against the catalog.
      </p>
      <div className="flex gap-2">
        <input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ask()}
          placeholder="Ask about a companion rule…"
          className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
        />
        <button
          onClick={ask}
          disabled={loading || !message.trim()}
          className="flex items-center gap-1 rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Ask
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}
      {saveNote && (
        <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {saveNote}
        </div>
      )}

      {verdict && style && (
        <div className={`mt-3 space-y-2 rounded border px-3 py-2 ${style.ring}`}>
          <div className="flex items-start gap-2 text-sm text-slate-800">
            {style.icon}
            <span>{verdict.message}</span>
          </div>
          <Term label="Trigger" term={verdict.antecedent} />
          <Term label="Add" term={verdict.consequent} />
          <Term label="Unless present" term={verdict.guard_unless} />
          <Term label="Only if present" term={verdict.guard_only_if} />
          {verdict.existing_rules.length > 0 && (
            <div className="text-xs text-slate-600">
              Matches:{' '}
              {verdict.existing_rules
                .map((r) => `${r.name} (${r.status}${r.is_active ? ', live' : ''})`)
                .join(' · ')}
            </div>
          )}
          {verdict.drafted_rule && (
            <button
              onClick={saveDraft}
              disabled={loading}
              className="mt-1 flex items-center gap-1 rounded bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50"
            >
              <PlusCircle className="h-3.5 w-3.5" />
              Save as proposed rule
            </button>
          )}
        </div>
      )}
    </section>
  );
}
